import React from "react";
import {Button} from "../../../components";
import "./_form-footer.scss";
import {NavItem} from "../FormNav/NavItem";
import {navItemSelector, setActiveNavItem, setSaveClicked} from "../../slices/NavigationSlice";
import {NavItemType} from "../FormNav/NavItemType";
import {scrollToTop} from "../../utils/FormContentUtils";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useHistory, useParams} from "react-router-dom";
import {RouteWithId} from "../../../routes/types";

interface FormFooterProps {
    navConfig: NavItem[],
    validateForm: () => boolean,
    saveForm: (exitForm: boolean) => Promise<boolean>,
    pageConfig: NavItem[];
}

// Helper to extract and format the estate item type from pathTemplate
const getEstateItemType = (pathTemplate?: string): string => {
    if (!pathTemplate) return '';
    const match = /\/(IrrevocableTrust|RevocableTrust|Will|Miscellaneous)\//.exec(pathTemplate);
    if (!match) return '';
    const type = match[1];
    if (type === 'IrrevocableTrust') return 'irrevocable trust';
    if (type === 'RevocableTrust') return 'revocable trust';
    return type.toLowerCase(); // 'will'
};

const FormFooter = ({navConfig, validateForm, saveForm, pageConfig}: FormFooterProps) => {
    const dispatch = useAppDispatch();
    const activeNavItem = useAppSelector(navItemSelector);
    const {id} = useParams<RouteWithId>();
    const history = useHistory();

    const getNextLabel = () => {
        let presentPage;
        if (activeNavItem?.type === NavItemType.PAGE) {
            presentPage = pageConfig.find((page) => page.index === activeNavItem?.index)?.index;
        } else {
            presentPage = pageConfig.find((page) => page.index === activeNavItem?.parent?.index)?.index;
        }
        if (presentPage !== undefined) {
            if (presentPage === pageConfig.length - 1) {
                const EstateItemType = getEstateItemType(pageConfig[0].pathTemplate).toUpperCase();
                return `GENERATE ${EstateItemType}`;
            } else {
                return pageConfig[presentPage + 1].displayName.toUpperCase();
            }
        }
    }

    const isPreviousButtonDisabled = () => {
        const activePageIndex = activeNavItem?.type === NavItemType.PAGE ?
            activeNavItem.index : activeNavItem?.parent?.index;
        return activePageIndex === 0
    }

    const isLastPage = () => {
        const activePageIndex = activeNavItem?.type === NavItemType.PAGE ?
            activeNavItem.index : activeNavItem?.parent?.index;
        return activePageIndex === navConfig.length - 1;
    }

    const movePage = (pageMove: number) => {
        if (activeNavItem) {
            let currentIndex: number | undefined = activeNavItem.index;
            if (activeNavItem.type === NavItemType.LINK) {
                currentIndex = activeNavItem.parent?.index;
            }
            const newIndex = currentIndex !== undefined ? currentIndex + pageMove : 0;

            if (currentIndex !== undefined && newIndex < currentIndex) {
                dispatch(setSaveClicked(false));
            }

            const newPage = navConfig.find((page) => page.index === newIndex);
            dispatch(setActiveNavItem(newPage));
            setTimeout(() => {
                scrollToTop("auto");
            }, 10);
        }
    }

    const onClickPrevious = () => {
        movePage(-1);
    }

    const onClickNext = () => {
        dispatch(setSaveClicked(true));
        if (validateForm()) {
            saveForm(false)
                .then(() => {
                    dispatch(setSaveClicked(false));
                    movePage(1);
                })
        }
    }

    const onClickSave = () => {
        dispatch(setSaveClicked(true));
        if (validateForm()) {
            saveForm(true).then(() => {
                dispatch(setSaveClicked(false));
                history.push({
                    pathname: `/Profile/${id}/ClientProfile/EstateFlowchart`,
                });
                dispatch(setActiveNavItem(undefined)); // Reset to the first page after saving
            });
        }
    }

    return (
        <div className="form-footer textalign-right" data-testid="revtrust-form-footer">
            <div className="prev-btn-container">
                <Button
                    icon="none"
                    id="prev_page_button"
                    includeRef={false}
                    kind="secondary"
                    onClick={onClickPrevious}
                    size="medium"
                    disabled={isPreviousButtonDisabled()}
                    type="button"
                >
                    PREVIOUS
                </Button>
            </div>
            <div className="save-next-btn-container">
                <Button
                    icon="none"
                    id="save_new_client_profile_button"
                    includeRef={false}
                    kind="secondary"
                    onClick={onClickSave}
                    size="medium"
                    type="button"
                >
                    SAVE PROGRESS
                </Button>
                <Button
                    icon="right"
                    iconName="arrow_right"
                    id="next_page_button"
                    kind="primary"
                    onClick={isLastPage() ? onClickSave : onClickNext}
                    disabled={false}
                    size="medium"
                    type="button"
                    data-testid="NextPage-btn"
                >
                    {getNextLabel()}
                </Button>
            </div>
        </div>
    )
}

export default FormFooter;